import { tokens, Stack, Icon } from '@/design';
import { useViewerStore } from '@/state/useViewerStore';

/**
 * CostBudgetSettings — WP-E9 cost budget route scaffold.
 * 
 * Displays current cost budget settings and usage for the organization.
 * Links to billing surface for detailed management.
 */
export function CostBudgetSettings() {
  const mockCostBudgetState = useViewerStore((state) => state.mockCostBudgetState);
  
  const percentage = mockCostBudgetState 
    ? (mockCostBudgetState.used / mockCostBudgetState.total) * 100
    : 0;

  return (
    <div
      data-gvw-id="GVW-cost-budget"
      style={{
        maxWidth: tokens.layout.popoverLg * 2.22 + 'px',
        margin: '0 auto',
        padding: tokens.space[4],
        fontFamily: tokens.font.family.ui,
      }}
    >
      <Stack direction="column" gap={4}>
        <header>
          <Stack direction="row" align="center" gap={2}>
            <Icon name="payments" size={24} decorative />
            <h1 style={{
              margin: 0,
              fontSize: tokens.font.size.lg,
              fontWeight: tokens.font.weight.medium,
              color: tokens.color.ink.primary,
            }}>
              Cost Budget
            </h1>
          </Stack>
          <p style={{
            margin: `${tokens.space[2]} 0 0`,
            color: tokens.color.ink.secondary,
            fontSize: tokens.font.size.sm,
          }}>
            Monitor and manage copilot usage costs for your organization.
          </p>
        </header>

        {mockCostBudgetState ? (
          <Stack direction="column" gap={3}>
            <div style={{
              padding: tokens.space[4],
              background: tokens.color.surface[1],
              borderRadius: tokens.radius.input,
              border: tokens.border.thin,
            }}>
              <Stack direction="column" gap={3}>
                <div>
                  <h2 style={{
                    margin: 0,
                    fontSize: tokens.font.size.sm,
                    fontWeight: tokens.font.weight.medium,
                    color: tokens.color.ink.primary,
                  }}>
                    Current Usage
                  </h2>
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: tokens.space[3],
                }}>
                  <div style={{
                    flex: 1,
                    position: 'relative',
                    height: tokens.space[3] + 'px',
                    background: tokens.color.surface[0],
                    borderRadius: tokens.radius.sm,
                    overflow: 'hidden',
                    border: tokens.border.thin,
                  }}>
                    {/* 80% warning notch */}
                    <div style={{
                      position: 'absolute',
                      left: '80%',
                      top: `${-tokens.space[0] - 2}px`,
                      width: `${tokens.space[0] + 2}px`,
                      height: `${tokens.space[4]}px`,
                      background: tokens.color.ink.secondary,
                      opacity: 0.7,
                    }} />
                    
                    {/* Progress fill */}
                    <div style={{
                      width: `${Math.min(100, percentage)}%`,
                      height: '100%',
                      background: percentage >= 100 
                        ? 'var(--md-error)' 
                        : percentage >= 80 
                        ? 'var(--md-warning)' 
                        : 'var(--md-primary)',
                      borderRadius: tokens.radius.sm,
                    }} />
                  </div>
                  
                  <div style={{
                    minWidth: tokens.layout.popoverSm * 0.43 + 'px',
                    textAlign: 'right',
                    fontSize: tokens.font.size.sm,
                  }}>
                    {mockCostBudgetState.used} of {mockCostBudgetState.total} runs
                  </div>
                </div>

                <Stack direction="row" justify="space-between">
                  <div>
                    <span style={{ 
                      fontSize: tokens.font.size.xs,
                      color: tokens.color.ink.secondary 
                    }}>
                      Budget window: {mockCostBudgetState.window}
                    </span>
                  </div>
                  <div>
                    <span style={{ 
                      fontSize: tokens.font.size.xs,
                      color: tokens.color.ink.secondary 
                    }}>
                      {Math.round(percentage)}% used
                    </span>
                  </div>
                </Stack>

                {mockCostBudgetState.resetTime && (
                  <div style={{
                    fontSize: tokens.font.size.xs,
                    color: tokens.color.ink.secondary,
                  }}>
                    Next reset: {mockCostBudgetState.resetTime}
                  </div>
                )}
              </Stack>
            </div>

            <div style={{
              padding: tokens.space[3],
              background: tokens.color.surface[1],
              borderRadius: tokens.radius.input,
              border: tokens.border.thin,
            }}>
              <Stack direction="row" align="center" gap={2}>
                <Icon name="info" size={16} decorative />
                <div style={{ fontSize: tokens.font.size.sm }}>
                  Expensive operations require confirmation when approaching the budget limit.
                </div>
              </Stack>
            </div>

            <Stack direction="row" gap={2}>
              <button
                type="button"
                onClick={() => {
                  console.log('[Cost Budget] Open billing surface');
                }}
                style={{
                  minHeight: tokens.density.touchMin,
                  border: 'none',
                  borderRadius: tokens.radius.md,
                  background: 'var(--md-primary)',
                  color: 'var(--md-on-primary)',
                  paddingInline: tokens.space[4],
                  cursor: 'pointer',
                  fontSize: tokens.font.size.sm,
                  fontWeight: tokens.font.weight.medium,
                }}
              >
                <Icon name="receipt_long" size={16} decorative style={{ marginRight: tokens.space[1] }} />
                View billing details
              </button>
              
              <button
                type="button"
                onClick={() => {
                  console.log('[Cost Budget] Open cost settings');
                }}
                style={{
                  minHeight: tokens.density.touchMin,
                  border: tokens.border.thin,
                  borderRadius: tokens.radius.md,
                  background: 'transparent',
                  color: tokens.color.ink.primary,
                  paddingInline: tokens.space[4],
                  cursor: 'pointer',
                  fontSize: tokens.font.size.sm,
                  fontWeight: tokens.font.weight.medium,
                }}
              >
                <Icon name="settings" size={16} decorative style={{ marginRight: tokens.space[1] }} />
                Budget settings
              </button>
            </Stack>
          </Stack>
        ) : (
          <div style={{
            padding: tokens.space[4],
            background: tokens.color.surface[1],
            borderRadius: tokens.radius.input,
            border: tokens.border.thin,
            textAlign: 'center',
          }}>
            <Stack direction="column" gap={2} align="center">
              <Icon name="monitoring" size={32} decorative />
              <div style={{
                fontSize: tokens.font.size.sm,
                color: tokens.color.ink.secondary,
              }}>
                Cost budget data not available. Backend stream not deployed.
              </div>
            </Stack>
          </div>
        )}
      </Stack>
    </div>
  );
}